import { ErrorResponse } from "./errors"
import { logtoConfig } from "./logto/logto"
import * as jose from 'jose'
// Types
export interface AccessToken {
    jti: string
    exp: number
    iat: number
    iss: string
    sub: string
    aud: string
    scope: string
    client_id: string
    scopes: string[]
    organization_id?: string // Organization ID if this is an organization-scoped token
  }
  
  export interface UserContext {
    sub: string
    scopes: string[]
  }
  
  export interface OrgContext {
    id: string
    scopes: string[]
  }
  

export function decodeToken(token: string): AccessToken {
    const payload = jose.decodeJwt(token)
    
      
      const accessTokenScopes = typeof payload.scope === 'string' ? payload.scope.split(' ') : []
      
    // Extract organization ID from audience if it's an organization token
    let orgId: string | undefined
    if (payload.aud && typeof payload.aud === 'string' && payload.aud.startsWith('urn:logto:organization:')) {
      orgId = payload.aud.replace('urn:logto:organization:', '')
    } else if (payload.organization_id) {
      orgId = payload.organization_id as any
    }
      
      return {
        jti: payload.jti!,
        exp: payload.exp!,
        iat: payload.iat!,
        iss: payload.iss!,
        sub: payload.sub!,
        aud: payload.aud as string,
        scope: payload.scope as string,
        client_id: payload.client_id as string,
        scopes: accessTokenScopes,
        organization_id: orgId,
      }
}

/**
 * Verify JWT token and extract user information
 */
async function validateJSONToken(token: string, audience: string): Promise<AccessToken> {
    const jwksUri = new URL(logtoConfig.endpoint + '/oidc/jwks')
    const JWKS = jose.createRemoteJWKSet(jwksUri)
  
  
    const { payload } = await jose.jwtVerify(token, JWKS, {
      issuer: new URL(logtoConfig.endpoint + '/oidc').toString(),
      audience: audience,
    })
  
    
    const accessTokenScopes = typeof payload.scope === 'string' ? payload.scope.split(' ') : []
    
    // Extract organization ID from audience if it's an organization token
    let orgId: string | undefined
    if (audience.startsWith('urn:logto:organization:')) {
      orgId = audience.replace('urn:logto:organization:', '')
    } else if (payload.organization_id) {
      orgId = payload.organization_id as any
    }
    
    return {
      jti: payload.jti!,
      exp: payload.exp!,
      iat: payload.iat!,
      iss: payload.iss!,
      sub: payload.sub!,
      aud: payload.aud as string,
      scope: payload.scope as string,
      client_id: payload.client_id as string,
      scopes: accessTokenScopes,
      organization_id: orgId,
      
    }
  }
  
  async function validateAccessToken(token: string): Promise<AccessToken> {
    return await validateJSONToken(token, logtoConfig.resources![0]!)
  }
  
  async function validateOrgToken(token: string, orgId: string): Promise<AccessToken> {
    return await validateJSONToken(token, "urn:logto:organization:" + orgId)
  }
  
  /**
   * Verify JWT token and extract user information
   */
  export async function verifyUserToken(token: string): Promise<AccessToken> {
    try {
      return await validateAccessToken(token)
    } catch (error) {
      console.error('verifyUserToken error', error)
      throw new ErrorResponse('Invalid or expired access token', 401, '46E509C9-03C3-4926-A199-A6995EC2A258')
    }
  }
  
  /**
   * Verify organization token and extract organization information
   */
  export async function verifyOrgToken(token: string, orgId: string): Promise<AccessToken > {
    try {
      return await validateOrgToken(token, orgId)
    } catch (error) {
      throw new ErrorResponse('Invalid or expired organization token', 401, "7428EB41-B9B1-475A-A0F2-8F1E4DA9A51B")
    }
  }

  /** Verify a JWT against an arbitrary audience (e.g. a dedicated MCP resource). */
  export async function verifyResourceToken(token: string, audience: string): Promise<AccessToken> {
    try {
      return await validateJSONToken(token, audience)
    } catch (error) {
      console.error('verifyResourceToken error', error)
      throw new ErrorResponse('Invalid or expired access token', 401, 'B2D1F0A3-8C47-4E6A-9D15-3A7B2E8F1C09')
    }
  }
  